/**
 * The type-specific half of an `estimate_value` agent's settings page: which
 * values it works on, the value kind, where an accepted value goes, and its
 * floors. Which values it works on is each value's own AI setting
 * (value_ai_modes, migration 920) — the same setting Settings → Attributes
 * shows — so the two pages can never disagree. The rest is stored in
 * agent_settings.behavior_config (migration 821).
 *
 * The Value Estimator and every custom agent made on its behavior show this
 * same panel; the rest of the page is the template's common switches.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useValueAiSettingsStore } from '../../../stores/prioritization/valueAiSettingsStore';
import { setValueAiSetting } from '../../../lib/valueAiService';
import { Button } from '../../ui/Button';
import { Select, Switch } from '../../ds';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { resolveEstimatorConfig } from '../../../lib/valueEstimatorRunner';
import type { ValueEstimatorBehaviorConfig } from '../../../lib/ai/agentDefinitions';
import type { BehaviorSettingsPanelProps } from './types';

export function EstimateValueSettings({ agentType, settings, isSaving, saveBehaviorConfig }: BehaviorSettingsPanelProps) {
  const attributes = useAttributesStore((s) => s.attributes);
  const behavior = resolveEstimatorConfig(settings);
  const saveBehavior = (patch: Partial<ValueEstimatorBehaviorConfig>) => saveBehaviorConfig({ ...behavior, ...patch });

  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const loadValueAi = useValueAiSettingsStore((s) => s.load);
  const valueAi = useValueAiSettingsStore((s) => s.settings);
  const agentName = useValueAiSettingsStore((s) => s.agentName);
  const putValueAi = useValueAiSettingsStore((s) => s.put);
  useEffect(() => {
    if (workspaceId) void loadValueAi(workspaceId);
  }, [workspaceId, loadValueAi]);

  const settingOf = (key: string) => valueAi.find((v) => v.attributeKey === key) ?? null;
  const worksOn = (key: string) => {
    const s = settingOf(key);
    return !!s && s.mode !== 'off' && s.agentType === agentType;
  };

  // On: this agent recommends the value (a value it already provides keeps that).
  // Off: AI off for the value. Another agent's value is taken over only by a click here.
  const toggleAttribute = async (key: string) => {
    if (!workspaceId) return;
    const current = settingOf(key);
    const on = worksOn(key);
    const { setting } = await setValueAiSetting({
      workspaceId,
      attributeKey: key,
      mode: on ? 'off' : 'recommend',
      agentType: on ? null : agentType,
      acceptFloor: current?.acceptFloor ?? 0.8,
      reason: on ? 'Turned off from the agent page' : 'Turned on from the agent page',
    });
    if (setting) putValueAi(setting);
  };

  const candidateAttributes = attributes.filter((a) => !a.isCustomField && (a.scales?.length || a.options?.length));

  return (
    <div className="card space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">What it estimates</h3>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">The settings that belong to this type of agent. Owner rulings 2026-10-13: effort first; estimate by default; the workspace is the boundary.</p>
      </div>
      <div>
        <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Values it works on</p>
        <p className="text-xs text-[var(--text-ter)] mb-2">
          Each value's AI setting (also on Settings → Attributes). Turning one on here makes this agent recommend it;
          to let it provide a value, open that attribute's settings.
        </p>
        <div className="flex flex-wrap gap-2">
          {candidateAttributes.map((a) => {
            const on = worksOn(a.key);
            const s = settingOf(a.key);
            const other = s && s.mode !== 'off' && s.agentType !== agentType ? agentName(s.agentType) : null;
            return (
              <Button key={a.key} variant={on ? 'primary' : 'secondary'} size="sm" disabled={isSaving} onClick={() => void toggleAttribute(a.key)} aria-pressed={on}
                title={other ? `Now uses ${other}; click to use this agent instead` : undefined}>
                {a.label}
                {on && s?.mode === 'provide' ? ' · provides' : ''}
                {other ? ` · uses ${other}` : ''}
              </Button>
            );
          })}
          {!candidateAttributes.length && <p className="text-xs text-[var(--text-ter)]">No attributes with scales yet. <Link to="/prioritization/settings/attributes" className="underline">Add scales</Link>.</p>}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Value kind</p>
          <div className="flex gap-2">
            {(['estimate', 'score'] as const).map((k) => (
              <Button key={k} variant={behavior.valueKind === k ? 'primary' : 'secondary'} size="sm" disabled={isSaving} onClick={() => void saveBehavior({ valueKind: k })} aria-pressed={behavior.valueKind === k}>
                {k === 'estimate' ? 'Estimate (aggregates with people\'s estimates)' : 'Score (an authoritative proposal)'}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Where an accepted effort value goes</p>
          <Select value={behavior.target} disabled={isSaving} onChange={(e) => void saveBehavior({ target: e.target.value as typeof behavior.target })} className="w-full">
            <option value="attribute">The effort attribute</option>
            <option value="capacity_units">Planning capacity units</option>
            <option value="both">Both</option>
          </Select>
          <p className="text-[11px] text-[var(--text-ter)] mt-1">Capacity units are written by the accept step once the sizing map exists (phase 2); the choice is stored now.</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Confidence floor: {Math.round(behavior.confidenceFloor * 100)}%</label>
          <input type="range" min={10} max={90} step={5} value={Math.round(behavior.confidenceFloor * 100)} disabled={isSaving}
            onChange={(e) => void saveBehavior({ confidenceFloor: Number(e.target.value) / 100 })} className="w-full accent-[var(--slate)]" />
          <p className="text-[11px] text-[var(--text-ter)]">Below this the agent abstains and says why.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Clarity floor: {behavior.clarityFloor}</label>
          <input type="range" min={20} max={90} step={5} value={behavior.clarityFloor} disabled={isSaving}
            onChange={(e) => void saveBehavior({ clarityFloor: Number(e.target.value) })} className="w-full accent-[var(--slate)]" />
          <p className="text-[11px] text-[var(--text-ter)]">Items thinner than this abstain before any model call.</p>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-[var(--text-sec)]">Allow model-only proposals</p>
            <p className="text-[11px] text-[var(--text-ter)]">With no precedent or document, still propose — marked "no precedent found".</p>
          </div>
          <Switch checked={behavior.allowModelOnly} disabled={isSaving} aria-label="Allow model-only proposals" onChange={() => void saveBehavior({ allowModelOnly: !behavior.allowModelOnly })} />
        </div>
      </div>
    </div>
  );
}
